import { ui } from './stores.js';
import { get } from 'svelte/store';

// Replaced at build time with the same id the service worker and the asset
// manifest are stamped with (see BUILD_ID in rollup.config.js), so the page can
// tell whether a newly installed worker actually carries newer code than the
// code the page is already running. Left as the literal placeholder in dev,
// where service workers are unregistered anyway.
const BUILD_VERSION = '__BUILD_VERSION__';
const EXPECTED_SW_VERSION = `stcc-pwa-${BUILD_VERSION}`;
const VERSION_QUERY_TIMEOUT = 2000;

let waitingWorker = null;
let reloadingForUpdate = false;
let updateRequested = false;
let registration = null;
let registered = false;
// Both latch once and are never cleared. They have to live here rather than be
// read off the ui store, because resetStores() rebuilds ui without loadedStatus
// at the start of every game — so the store says "still booting" for the whole
// of a game, and a mid-game update would reload out from under the player.
let booted = false;
let reachedSetup = false;

function isLocalDevelopment(){
    return ['localhost', '127.0.0.1', '::1'].includes(window.location.hostname)
        && !new URLSearchParams(window.location.search).has('pwa');
}

async function unregisterLocalServiceWorkers(){
    let registrations = await navigator.serviceWorker.getRegistrations();
    await Promise.all(registrations
        .filter(registration => registration.scope.startsWith(window.location.origin))
        .map(registration => registration.unregister()));
    let cacheNames = await caches.keys();
    await Promise.all(cacheNames
        .filter(name => name.startsWith('stcc-pwa-'))
        .map(name => caches.delete(name)));
}

function canReloadForUpdate(){
    // Still on the Loading... screen: a reload is invisible, and the card
    // database load that it would repeat has not finished yet.
    if(!booted) return true;
    // Up and sitting on setup, past the initial load — between games.
    return reachedSetup && get(ui).tab == 'setup';
}

function reloadForUpdate(){
    if(reloadingForUpdate) return;
    reloadingForUpdate = true;
    window.location.reload();
}

// Ask a worker which build it carries. The worker may only be installed rather
// than controlling, so it has no client to post back to: hand it a port and let
// it answer on that.
function askWorkerVersion(worker){
    return new Promise((resolve) => {
        let settled = false;
        let finish = (version) => {
            if(settled) return;
            settled = true;
            resolve(version);
        };
        try{
            let channel = new MessageChannel();
            channel.port1.onmessage = (event) => {
                if(event.data?.type == 'STCC_SW_VERSION') finish(event.data.version);
            };
            worker.postMessage({type: 'GET_VERSION'}, [channel.port2]);
        }catch(error){
            finish(null);
            return;
        }
        setTimeout(() => finish(null), VERSION_QUERY_TIMEOUT);
    });
}

async function markUpdateReady(worker){
    // Navigations are network-first and the bundle name is content-hashed, so a
    // load that happened after the deploy is *already* running this build — the
    // worker is only now catching up with the page. Reloading would re-fetch
    // identical files and repeat the card database load for nothing, which is
    // what made the first launch after every update load twice.
    let version = await askWorkerVersion(worker);
    if(version == EXPECTED_SW_VERSION){
        // Let it activate anyway: that prunes the previous build's caches and
        // puts this page on the fresh ones for whatever it loads from here on.
        worker.postMessage({type: 'SKIP_WAITING'});
        return;
    }

    // Versions genuinely differ (booted from cache while offline, or this tab
    // has been open across a deploy), or the worker never answered.
    waitingWorker = worker;
    // A worker that did not answer may be old code predating the version query.
    // Assume an update is pending, but leave it to the user rather than
    // reloading on a guess.
    if(version && canReloadForUpdate()){
        applyPWAUpdate();
        return;
    }
    ui.update(u => {
        u.pwaUpdateReady = true;
        return u;
    });
}

export async function applyPWAUpdate(){
    updateRequested = true;
    if(!waitingWorker){
        reloadForUpdate();
        return;
    }
    waitingWorker.postMessage({type: 'SKIP_WAITING'});
    // controllerchange normally gets there first; this is the backstop.
    setTimeout(() => {
        reloadForUpdate();
    }, 1500);
}

ui.subscribe(u => {
    if(!booted){
        // The Loading... screen is up for as long as loadedStatus is unset or
        // 'not'. Latch the first moment it is neither, however the app got
        // there: loadAll() reaching 'live', or an offline restore to 'cache'.
        if(u.loadedStatus && u.loadedStatus != 'not') booted = true;
        return;
    }
    if(!reachedSetup){
        // The first arrival at setup is the tail end of the initial load, not a
        // return to it — loadAll() sets loadedStatus and *then* the tab — and
        // applying an update there is precisely the reload worth avoiding.
        if(u.tab == 'setup') reachedSetup = true;
        return;
    }
    if(waitingWorker && u.tab == 'setup') applyPWAUpdate();
});

export async function registerPWA(){
    if(!('serviceWorker' in navigator)) return;
    if(isLocalDevelopment()){
        await unregisterLocalServiceWorkers();
        return;
    }
    if(registered) return;
    registered = true;

    window.addEventListener('beforeinstallprompt', (event) => {
        event.preventDefault();
        ui.update(u => {
            u.installPrompt = event;
            return u;
        });
    });

    navigator.serviceWorker.addEventListener('controllerchange', () => {
        // Only ever a consequence of our own SKIP_WAITING for an update we chose
        // to apply. A first install claiming a page that had no controller, or a
        // same-build worker taking over, replaces nothing that is on screen.
        if(!updateRequested) return;
        reloadForUpdate();
    });

    navigator.serviceWorker.addEventListener('message', (event) => {
        if(event.data?.type == 'STCC_SW_VERSION'){
            ui.update(u => {
                u.pwaVersion = event.data.version;
                return u;
            });
        }
    });

    window.addEventListener('online', () => {
        registration?.update();
    });

    try{
        registration = await navigator.serviceWorker.register('/service-worker.js');

        if(registration.waiting) markUpdateReady(registration.waiting);

        registration.addEventListener('updatefound', () => {
            let worker = registration.installing;
            if(!worker) return;
            worker.addEventListener('statechange', () => {
                if(worker.state == 'installed' && navigator.serviceWorker.controller){
                    markUpdateReady(worker);
                }
            });
        });

        if(registration.active){
            registration.active.postMessage({type: 'GET_VERSION'});
        }

        if(navigator.onLine) registration.update();
        setInterval(() => {
            if(navigator.onLine) registration.update();
        }, 60 * 60 * 1000);
    }catch(error){
        console.warn('Unable to register service worker', error);
    }
}
